import { getActiveProfileTable } from "building-generation/elements/profileTools";
import { getOpeningRails } from "building-query/trims/openings/openingTrims";
import { TrimKeyMeasureCondition } from "building-query/trims/trimTypes";
import type { Building } from "building-types/building/buildingTypes";
import type {
  MillworkKAM,
  MillworkKAMOptions,
  MillworkKeyAttributes,
  MillworkKeyMeasures,
  OpeningTrimAttributes,
  OpeningTrimKAM,
  OpeningTrimKAMOptions,
  OpeningTrimMeasures,
} from "building-types/dataSequences/builtin/MillworkKAM";
import type { BuildingEntityFilter, TrimEntity } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import { buildingEntityPickHandle } from "building-types/entities/buildingEntityPickHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import type { Room } from "building-types/rooms/roomTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { keyAttributeNew, keyMeasureNew, keyMeasureOptionsNew } from "doc/utils/dataSequenceTools";
import { planeNewXY } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonUnion } from "geom/polyclip/polyClip";
import { polyLine2Length } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { getRailsLength } from "./roofs";

export type MillworkSubEntity = "MILLWORK" | "OPENING_TRIM";

interface TrimKBMData {
  id: string;
  type: "PERIMETER" | "CROWN" | "BASE" | "WINDOW" | "DOOR";
  name: string;
  length: number;
  level: string;
  profile?: string;
  rails: PolyLine2[];
  plane?: Plane;
  roomId?: string;
  rooms?: Room[];
  handles: DocEntityHandle[];
}

/** @deprecated - use the Building Query API instead */
export function getTrimFromBuilding(building: Building): TrimKBMData[] {
  const {
    levels,
    baseTrimMoldings,
    crownTrimMoldings,
    roomOutlineTable,
    settings: { profile, interiorTrim },
    enabledFeatures,
    doc: { profiles },
  } = building;
  const trimEntries: TrimKBMData[] = [];
  const roomMap: { [key: string]: TrimKBMData } = {};

  // start with room perimeter entries
  for (const level of Object.values(levels)) {
    const roomSetsByLevel = building.wallMasses[level.id].openConnectedRoomSets;
    const roomIds: string[] = [];

    for (const roomSet of Object.values(roomSetsByLevel)) {
      const ids = roomSet.map((room) => room.id);
      if (roomIds.indexOf(ids[0]) !== -1) {
        // skip if we've already processed this roomSet
        continue;
      }
      roomIds.push(...ids);
      const trimEntry: TrimKBMData = {
        id: ids.join("-"),
        type: "PERIMETER",
        name: "",
        length: 0,
        level: "",
        profile: ProfileExtrusionType.NONE,
        handles: [],
        rails: [],
        plane: planeNewXY(0, 0, level.floorElevation + 1),
        rooms: roomSet,
      };

      if (roomSet.length === 1) {
        const room = roomSet[0];
        const { nameInPlan, name, level, type } = room;
        if (type === "CHASE" || type === "NONE") {
          continue;
        }

        const roomOutline = ensureDefined(roomOutlineTable[room.id].INSIDE_OF_MASS_IGNORE_VOID);

        trimEntry.name = nameInPlan || (name && name.startsWith("Room") ? type : name);
        trimEntry.level = level.name;
        trimEntry.handles.push(buildingEntityPickHandle(room));
        trimEntry.length = polyLine2Length(roomOutline);
        trimEntry.rails.push(roomOutline);
        // store the room in the map by reference
        roomMap[room.id] = trimEntry;
      } else {
        const roomNames: string[] = [];
        const perims: PolyLine2[] = [];
        let perimeterFull: number = 0;

        roomSet.forEach((room) => {
          const { id, type, nameInPlan, name, level } = room;
          const n = nameInPlan || (name && name.startsWith("Room") ? type : name);
          if (roomNames.indexOf(n) === -1) {
            roomNames.push(n);
          }
          // store the trimEntry in the map by reference
          roomMap[room.id] = trimEntry;

          if (!trimEntry.level) {
            trimEntry.level = level.name;
          }
          trimEntry.handles.push(buildingEntityPickHandle(room));
          // perimeters in a group.
          perims.push(ensureDefined(roomOutlineTable[id].INSIDE_OF_MASS_IGNORE_VOID));
        });
        const combinedPolygon = polygonUnion({ pls: perims.map((x) => [x]) }).flat();
        combinedPolygon.forEach((poly) => {
          trimEntry.rails.push(poly);
          perimeterFull += polyLine2Length(poly);
        });

        if (!trimEntry.name) {
          trimEntry.name = roomNames.sort().join(", ") + " [" + roomSet.length + "]";
        }
        trimEntry.length = perimeterFull;
      }
      // add an entry for full room perimeter
      trimEntries.push(trimEntry);
    }
  }

  const profileTable = getActiveProfileTable({
    settingsToCatalogs: enabledFeatures.settingsToCatalogs,
    profiles,
    profileSettings: profile,
  });
  for (const trim of Object.values(baseTrimMoldings)) {
    const { roomSet, trimRails } = trim;
    const baseTrim = getRailsLength(trimRails);
    const refEntry = roomMap[roomSet[0].id];
    if (!refEntry) {
      continue;
    }
    const trimEntry: TrimKBMData = {
      ...refEntry,
      type: "BASE",
      length: baseTrim,
      profile: interiorTrim.baseBoardType
        ? profileTable[interiorTrim.baseBoardType]?.name ?? interiorTrim.baseBoardType
        : interiorTrim.baseBoardType,
      rails: trimRails,
    };
    // add BASE trim records
    trimEntries.push(trimEntry);
  }

  for (const trim of Object.values(crownTrimMoldings)) {
    const { roomSet, trimRails } = trim;
    const crownTrim = getRailsLength(trimRails);
    if (crownTrim === 0) {
      continue;
    }
    const refEntry = roomMap[roomSet[0].id];
    if (!refEntry) {
      continue;
    }

    const crownEntry: TrimKBMData = {
      ...refEntry,
      type: "CROWN",
      length: crownTrim,
      profile: interiorTrim.crownMoldingType
        ? profileTable[interiorTrim.crownMoldingType]?.name ?? interiorTrim.crownMoldingType
        : interiorTrim.crownMoldingType,
      rails: trimRails,
    };

    // add CROWN trim records
    trimEntries.push(crownEntry);
  }

  return trimEntries;
}

export function makeMillworkKAM(trim: TrimKBMData): MillworkKAM {
  return {
    type: "MILLWORK",
    attributes: {
      level: keyAttributeNew(trim.level),
      type: keyAttributeNew(trim.type),
      id: keyAttributeNew(trim.id),
      roomName: keyAttributeNew(trim.name),
      profile: keyAttributeNew(trim.profile ?? ""),
      entityName: keyAttributeNew(`${trim.type}-${trim.profile}-${trim.id}`),
    },
    measures: {
      length: keyMeasureNew(trim.length, "FEET", "INCHES"),
      count: keyMeasureNew(1),
    },
    handles: [...trim.handles],
  };
}
export const EMPTY_MILLWORK_KAM: MillworkKAM = {
  type: "MILLWORK",
  attributes: {
    level: keyAttributeNew(""),
    type: keyAttributeNew(""),
    id: keyAttributeNew(""),
    roomName: keyAttributeNew(""),
    profile: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET", "INCHES"),
    count: keyMeasureNew(0),
  },
  handles: [],
};
export function makeDefaultMillworkKAMOptions(): MillworkKAMOptions {
  return {
    attributes: {},
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}
export const defaultMillworkKAMSort = (a: MillworkKAM, b: MillworkKAM) => {
  return (
    a.attributes.level.value.localeCompare(b.attributes.level.value) ||
    a.attributes.roomName.value.localeCompare(b.attributes.roomName.value) ||
    b.attributes.type.value.localeCompare(a.attributes.type.value)
  );
};

/** @deprecated - use the Building Query API instead */
export function buildingMillworkKAMs(building: Building, filter?: BuildingEntityFilter): MillworkKAM[] {
  const trims = getTrimFromBuilding(building);
  const result: MillworkKAM[] = [];
  for (const trim of trims) {
    if (filter) {
      const matchesLabel =
        trim.rooms?.some((room) => {
          const trimEntity: TrimEntity = { handles: [], room };
          return filter(trimEntity);
        }) ?? true;
      if (!matchesLabel) {
        continue;
      }
    }
    result.push(makeMillworkKAM(trim));
  }
  return result;
}

export const MILLWORK_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  MillworkKeyAttributes,
  MillworkKeyMeasures
> = {
  attributes: {
    id: {
      hidden: true,
    },
    entityName: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_MILLWORK: (keyof MillworkKeyAttributes)[] = ["type", "profile", "id"];

// OPENING_TRIM
/** @deprecated - use the Building Query API instead */
export function getOpeningTrimFromBuilding(building: Building): TrimKBMData[] {
  const { rooms } = building;
  const trimEntries: TrimKBMData[] = [];
  const openingTrims = getOpeningRails(building, undefined, TrimKeyMeasureCondition.INTERIOR);

  // add WINDOW and DOOR trims
  for (const trim of openingTrims) {
    const { condition, category, type, name, length, roomId, profileId, rails } = trim;

    const trimEntry: TrimKBMData = {
      id: `${condition}-${category}-${profileId ?? "DEFAULT_PROFILE"}`,
      type,
      name,
      length,
      level: roomId ? rooms[roomId]?.level?.name ?? "NONE" : "NONE",
      profile: profileId ?? "DEFAULT_PROFILE",
      rails,
      handles: trim.handles ?? [],
      roomId,
    };
    trimEntries.push(trimEntry);
  }

  return trimEntries;
}

export function makeOpeningTrimKAM(trim: TrimKBMData): OpeningTrimKAM {
  return {
    type: "OPENING_TRIM",
    attributes: {
      level: keyAttributeNew(trim.level),
      type: keyAttributeNew(trim.type),
      id: keyAttributeNew(trim.id),
      name: keyAttributeNew(trim.name),
      profile: keyAttributeNew(trim.profile ?? ""),
      entityName: keyAttributeNew(`${trim.type}-${trim.profile}-${trim.id}`),
    },
    measures: {
      length: keyMeasureNew(trim.length, "FEET", "INCHES"),
      count: keyMeasureNew(1),
    },
    handles: [...trim.handles],
  };
}
export const EMPTY_OPENING_TRIM_KAM: OpeningTrimKAM = {
  type: "OPENING_TRIM",
  attributes: {
    level: keyAttributeNew(""),
    type: keyAttributeNew(""),
    id: keyAttributeNew(""),
    name: keyAttributeNew(""),
    profile: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET", "INCHES"),
    count: keyMeasureNew(0),
  },
  handles: [],
};
export function makeDefaultOpeningTrimKAMOptions(): OpeningTrimKAMOptions {
  return {
    attributes: {},
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}
export const defaultOpeningTrimKAMSort = (a: OpeningTrimKAM, b: OpeningTrimKAM) => {
  return (
    a.attributes.level.value.localeCompare(b.attributes.level.value) ||
    b.attributes.type.value.localeCompare(a.attributes.type.value) ||
    a.attributes.name.value.localeCompare(b.attributes.name.value)
  );
};

/** @deprecated - use the Building Query API instead */
export function buildingOpeningTrimKAMs(building: Building, filter?: BuildingEntityFilter): OpeningTrimKAM[] {
  const trims = getOpeningTrimFromBuilding(building);
  const result: OpeningTrimKAM[] = [];
  for (const trim of trims) {
    if (filter) {
      const room = trim.roomId ? building.rooms[trim.roomId] : undefined;
      const trimEntity: TrimEntity = { handles: trim.handles ?? [], room };
      if (!filter(trimEntity)) {
        continue;
      }
    }
    result.push(makeOpeningTrimKAM(trim));
  }
  return result;
}

export const OPENING_TRIM_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  OpeningTrimAttributes,
  OpeningTrimMeasures
> = {
  attributes: {
    id: {
      hidden: true,
    },
    entityName: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_OPENING_TRIM: (keyof OpeningTrimAttributes)[] = ["type", "profile", "id"];
